// Remembering "where to insert" while an upload runs.
//
// A plain position is not enough: y-tiptap applies remote changes as a
// replacement of the whole document, so one character typed by someone else
// moves plain positions and widget decorations. A Yjs relative position keeps
// pointing at the same place across that replacement.

import {
  absolutePositionToRelativePosition,
  relativePositionToAbsolutePosition,
  ySyncPluginKey,
} from "@tiptap/y-tiptap";
import * as Y from "yjs";

/** The part of `EditorState` used here (structural). */
export interface DocPositionStateLike {
  plugins: unknown;
}

/** The mapping type of the y-tiptap functions (not exported by name). */
type ProsemirrorMapping = Parameters<
  typeof absolutePositionToRelativePosition
>[2];

/** The part of the y-sync plugin state used here. */
interface SyncState {
  type: Y.XmlFragment;
  doc: Y.Doc;
  binding: { mapping: ProsemirrorMapping } | null;
}

/**
 * The y-sync plugin state: undefined without the plugin, `binding: null` before
 * the binding exists. Callers treat both as unavailable.
 */
function syncState(state: DocPositionStateLike): SyncState | undefined {
  return ySyncPluginKey.getState(
    // The key's `getState` wants the full (DOM) `EditorState` type, which this
    // headless package does not have; the object passed fits structurally.
    state as unknown as Parameters<typeof ySyncPluginKey.getState>[0],
  ) as SyncState | undefined;
}

/**
 * A token for a position that survives collaborative changes. Null in an editor
 * without collaboration (the caller then uses the plain position).
 */
export function rememberDocPosition(
  state: DocPositionStateLike,
  pos: number,
): Uint8Array | null {
  const s = syncState(state);
  if (!s?.binding) return null;
  return Y.encodeRelativePosition(
    absolutePositionToRelativePosition(pos, s.type, s.binding.mapping),
  );
}

/** The current position of a token, or null when its place is gone. */
export function resolveDocPosition(
  state: DocPositionStateLike,
  token: Uint8Array,
): number | null {
  const s = syncState(state);
  if (!s?.binding) return null;
  return relativePositionToAbsolutePosition(
    s.doc,
    s.type,
    Y.decodeRelativePosition(token),
    s.binding.mapping,
  );
}
